import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ChevronRight, QrCode } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { WIZARD_STEPS } from "@/components/org/intake/wizard/wizardMeta";
import { BasicInfoStep } from "@/components/org/intake/wizard/BasicInfoStep";
import { FieldsStep } from "@/components/org/intake/wizard/FieldsStep";
import { IdentityStep } from "@/components/org/intake/wizard/IdentityStep";
import { SubmissionSettingsStep } from "@/components/org/intake/wizard/SubmissionSettingsStep";
import { ReviewStep } from "@/components/org/intake/wizard/ReviewStep";
import { LiveFormPreview } from "@/components/org/intake/wizard/LiveFormPreview";
import { QRCodeCreateModal } from "@/components/org/intake/qr/QRCodeCreateModal";
import { INTAKE_MODEL_STATUS_LABELS, INTAKE_MODEL_STATUS_STYLES } from "@/components/org/intake/intakeMeta";
import { ConnectionsSideNav } from "@/components/org/connections/ConnectionsSideNav";
import { CT } from "@/lib/connectionsTheme";

export default function IntakeModelWizardPage() {
  const { API } = useAuth();
  const { modelId } = useParams();
  const navigate = useNavigate();
  const isNew = modelId === "new";
  const [model, setModel] = useState(null);
  const [platformSmsOtpAvailable, setPlatformSmsOtpAvailable] = useState(true);
  const [step, setStep] = useState("basic");
  const [previewFields, setPreviewFields] = useState([]);
  const [error, setError] = useState("");
  const [modelQrCodes, setModelQrCodes] = useState([]);
  const [qrCreateOpen, setQrCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const { data } = await axios.get(`${API}/org/intake/models/${modelId}`, { withCredentials: true });
      setModel(data.model);
      setPreviewFields(data.model.fields || []);
      setPlatformSmsOtpAvailable(!!data.platform_sms_otp_available);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API, modelId, isNew]);

  useEffect(() => { load(); }, [load]);

  const loadModelQr = useCallback(async () => {
    if (!model || model.status !== "active") {
      setModelQrCodes([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/org/intake/qr-codes`, { params: { model_id: model.model_id }, withCredentials: true });
      setModelQrCodes(data.qr_codes);
    } catch {
      // non-critical for the wizard panel
    }
  }, [API, model]);

  useEffect(() => { loadModelQr(); }, [loadModelQr]);

  const handleCreated = (newModel) => {
    setModel(newModel);
    navigate(`/dashboard/connections/customer-intake/models/${newModel.model_id}`, { replace: true });
    setStep("fields");
  };

  const handleSaved = (updatedModel) => {
    setModel(updatedModel);
    if (updatedModel.fields) setPreviewFields(updatedModel.fields);
  };

  const goNext = (nextStep) => setStep(nextStep);

  if (error) {
    return (
      <div className={CT.page}>
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" data-testid="wizard-load-error">{error}</p>
      </div>
    );
  }

  if (!isNew && !model) {
    return <div className={CT.page}><p className="text-sm text-slate-500" data-testid="wizard-loading">Loading…</p></div>;
  }

  return (
    <div className={CT.page} data-testid="intake-model-wizard-page">
      <div className="flex flex-col gap-6 xl:flex-row">
        <ConnectionsSideNav activeKey="customer_intake" />

        <div className="min-w-0 flex-1 space-y-6">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="wizard-breadcrumb">
              <Link to="/dashboard/connections" className="hover:text-slate-900 dark:hover:text-slate-300">Connections</Link>
              <ChevronRight size={12} />
              <Link to="/dashboard/connections/customer-intake" className="hover:text-slate-900 dark:hover:text-slate-300">Customer Intake</Link>
              <ChevronRight size={12} />
              <span className="text-slate-700 dark:text-slate-300">{isNew ? "Create Intake Model" : model?.name}</span>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <h1 className={CT.h1}>{model ? model.name : "New Intake Model"}</h1>
              {model && (
                <span className={INTAKE_MODEL_STATUS_STYLES[model.status]} data-testid="wizard-model-status">
                  {INTAKE_MODEL_STATUS_LABELS[model.status]}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[220px_1fr_300px]">
            <div className="flex flex-row gap-2 overflow-x-auto lg:flex-col lg:gap-0" data-testid="wizard-stepper">
              {WIZARD_STEPS.map((s, idx) => {
                const unlocked = s.key === "basic" || !!model;
                const isLast = idx === WIZARD_STEPS.length - 1;
                return (
                  <div key={s.key} className="flex shrink-0 items-start gap-3 lg:flex-col lg:items-stretch">
                    <div className="flex items-center gap-3 lg:w-full">
                      <button
                        type="button"
                        disabled={!unlocked}
                        onClick={() => unlocked && goNext(s.key)}
                        data-testid={`wizard-stepper-${s.key}`}
                        className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors duration-150 ${
                          step === s.key ? "bg-blue-50 ring-1 ring-blue-200 dark:bg-blue-600/15 dark:ring-blue-500/40" : unlocked ? "hover:bg-slate-100 dark:hover:bg-white/5" : "cursor-not-allowed"
                        }`}
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            step === s.key ? "bg-blue-600 text-white" : unlocked ? "border border-blue-300 text-blue-600 dark:border-blue-500/30 dark:text-blue-300" : "border border-slate-200 text-slate-400 dark:border-white/10 dark:text-slate-600"
                          }`}
                        >
                          {s.number}
                        </span>
                        <span className={`text-xs font-bold ${step === s.key ? "text-slate-900 dark:text-white" : unlocked ? "text-slate-600 dark:text-slate-300" : "text-slate-400 dark:text-slate-600"}`}>{s.label}</span>
                      </button>
                    </div>
                    {!isLast && <div className="ml-[13px] hidden h-4 w-px bg-slate-200 dark:bg-white/10 lg:block" />}
                  </div>
                );
              })}
            </div>

            <div data-testid="wizard-step-content">
              {step === "basic" && <BasicInfoStep model={model} onCreated={handleCreated} onSaved={(m) => { handleSaved(m); goNext("fields"); }} />}
              {step === "fields" && model && <FieldsStep model={model} onFieldsChange={setPreviewFields} onSaved={(m) => { handleSaved(m); goNext("identity"); }} />}
              {step === "identity" && model && <IdentityStep model={model} platformSmsOtpAvailable={platformSmsOtpAvailable} onSaved={(m) => { handleSaved(m); goNext("submission"); }} />}
              {step === "submission" && model && <SubmissionSettingsStep model={model} onSaved={(m) => { handleSaved(m); goNext("review"); }} />}
              {step === "review" && model && <ReviewStep model={model} onSaved={handleSaved} />}
            </div>

            <div className="space-y-4 xl:sticky xl:top-4 xl:self-start">
              {step === "fields" && model && <LiveFormPreview fields={previewFields} modelName={model.name} />}
              <div className={`${CT.panel} p-5`} data-testid="wizard-qr-preview-panel">
                <p className={CT.label}>QR Code</p>
                {model?.status !== "active" ? (
                  <div className="mt-3 flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 p-6 text-center dark:border-white/15">
                    <QrCode size={32} className="text-slate-400 dark:text-slate-600" />
                    <p className="text-xs text-slate-500 dark:text-slate-500">Activate this Intake Model to unlock QR code generation.</p>
                  </div>
                ) : modelQrCodes.length === 0 ? (
                  <div className="mt-3 flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 p-6 text-center dark:border-white/15">
                    <QrCode size={32} className="text-slate-400 dark:text-slate-600" />
                    <button type="button" onClick={() => setQrCreateOpen(true)} data-testid="wizard-qr-generate-btn" className={`${CT.btnPrimary} mt-1`}>
                      Generate QR Code
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-col items-center gap-3">
                    <img
                      src={`${API}/org/intake/qr-codes/${modelQrCodes[0].qr_id}/image?fmt=png`}
                      alt="QR code"
                      className="h-28 w-28 rounded-lg border border-slate-200 bg-white p-1 dark:border-white/10"
                      data-testid="wizard-qr-thumbnail"
                    />
                    <Link to="/dashboard/connections/customer-intake?tab=qr" data-testid="wizard-qr-manage-link" className="text-xs font-bold text-blue-600 hover:underline dark:text-blue-400">
                      Manage in QR Codes tab
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {qrCreateOpen && model && (
        <QRCodeCreateModal
          models={[model]}
          fixedModelId={model.model_id}
          onClose={() => setQrCreateOpen(false)}
          onCreated={() => { setQrCreateOpen(false); loadModelQr(); }}
        />
      )}
    </div>
  );
}
